import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaInstagram } from "react-icons/fa";

/**
 * UploadPostStep
 * ------------------------------------------------------------------
 * Step 1 of the brand DM automation wizard. Collects the public
 * Instagram post URL the brand wants to automate, plus a friendly
 * title/description to identify the automation on the dashboard.
 *
 * The actual media-ID resolution against Instagram happens on the
 * backend at create-time, so this form intentionally stays simple –
 * we only sanity-check that the link looks like an Instagram post URL.
 */
const IG_POST_RE = /^https?:\/\/(www\.)?instagram\.com\/(p|reel|tv)\/[A-Za-z0-9_-]+/i;

const UploadPostStep = ({ data, onNext }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    mediaLink: "",
  });

  useEffect(() => {
    if (data) {
      setForm({
        title: data.title || "",
        description: data.description || "",
        mediaLink: data.mediaLink || "",
      });
    }
  }, [data]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const title = form.title.trim();
    const mediaLink = form.mediaLink.trim();

    if (!title) {
      toast.error("Please enter a post title");
      return;
    }
    if (!mediaLink) {
      toast.error("Please enter the Instagram post link");
      return;
    }
    if (!IG_POST_RE.test(mediaLink)) {
      toast.error(
        "Enter a valid Instagram URL (e.g. https://www.instagram.com/p/...)"
      );
      return;
    }

    onNext({
      uploadPost: {
        title,
        description: form.description.trim(),
        mediaLink,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <header>
        <h2 className="text-base md:text-xl font-semibold text-gray-900">
          Upload Post
        </h2>
        <p className="mt-1 text-[11px] md:text-sm text-gray-600">
          Pick the Instagram post you want to automate. We&apos;ll match this
          post to your connected brand account when comments come in.
        </p>
      </header>

      <div>
        <label className="block text-xs md:text-sm font-medium text-gray-700">
          Post Title
        </label>
        <input
          type="text"
          placeholder="e.g. Summer Drop Launch"
          value={form.title}
          onChange={handleChange("title")}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Internal label – only your team sees this.
        </p>
      </div>

      <div>
        <label className="block text-xs md:text-sm font-medium text-gray-700">
          Description{" "}
          <span className="text-[10px] text-gray-400">(optional)</span>
        </label>
        <textarea
          rows={3}
          placeholder="What is this automation for?"
          value={form.description}
          onChange={handleChange("description")}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        />
      </div>

      <div>
        <label className="block text-xs md:text-sm font-medium text-gray-700">
          Instagram Post Link
        </label>
        <div className="mt-1 flex items-center rounded-lg border border-gray-300 bg-white focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-100">
          <span className="pl-3 text-gray-400">
            <FaInstagram />
          </span>
          <input
            type="url"
            placeholder="https://www.instagram.com/p/xxxxxx/"
            value={form.mediaLink}
            onChange={handleChange("mediaLink")}
            className="w-full bg-transparent p-2 text-sm focus:outline-none"
          />
        </div>
        <p className="mt-1 text-[10px] text-gray-500">
          Use the public URL of the post (works for posts, reels and IGTV).
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default UploadPostStep;
